/**
 * Small, renderer-agnostic helpers for user-authored mixed Arabic/Latin text.
 *
 * These helpers never rewrite the stored value. They only establish a base
 * direction and isolate structured fields at the rendering boundary so an
 * Arabic company name cannot reorder the English location that follows it.
 */

const FIRST_ARABIC_LETTER_RE =
  /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
const FIRST_LATIN_LETTER_RE = /[A-Za-z]/;

const LEFT_TO_RIGHT_ISOLATE = "\u2066";
const RIGHT_TO_LEFT_ISOLATE = "\u2067";
const POP_DIRECTIONAL_ISOLATE = "\u2069";
const LEFT_TO_RIGHT_MARK = "\u200E";
const RIGHT_TO_LEFT_MARK = "\u200F";

export type BidiTextDirection = "ltr" | "rtl";

/** Resolve a field using the same first-strong-character rule as `dir=auto`. */
export function resolveBidiTextDirection(
  value: string | null | undefined,
  fallback: BidiTextDirection = "ltr",
): BidiTextDirection {
  if (!value) return fallback;
  const arabicAt = value.search(FIRST_ARABIC_LETTER_RE);
  const latinAt = value.search(FIRST_LATIN_LETTER_RE);
  if (arabicAt < 0 && latinAt < 0) return fallback;
  if (arabicAt < 0) return "ltr";
  if (latinAt < 0) return "rtl";
  return arabicAt < latinAt ? "rtl" : "ltr";
}

/**
 * Join structured values for a native/plain-text renderer without letting the
 * first field take control of the whole row. Unicode isolates are invisible
 * and are intentionally applied only to the rendered string, never persisted.
 */
export function joinBidiIsolatedText(
  values: ReadonlyArray<string | null | undefined>,
  separator: string,
  baseDirection: BidiTextDirection,
): string {
  const parts = values
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value));
  if (parts.length === 0) return "";

  const baseMark =
    baseDirection === "rtl" ? RIGHT_TO_LEFT_MARK : LEFT_TO_RIGHT_MARK;
  const isolated = parts.map((part) => {
    const isolate =
      resolveBidiTextDirection(part, baseDirection) === "rtl"
        ? RIGHT_TO_LEFT_ISOLATE
        : LEFT_TO_RIGHT_ISOLATE;
    return `${isolate}${part}${POP_DIRECTIONAL_ISOLATE}`;
  });

  return `${baseMark}${isolated.join(`${baseMark}${separator}${baseMark}`)}`;
}
